"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.usePickupPoints = void 0;
var _auth = require("@ori/auth");
var _checkoutUtils = require("@ori/checkout-utils");
var _configurationManager = require("@ori/configuration-manager");
var _fetching = require("@ori/fetching");
var _infinite = require("@ori/fetching/infinite");
var _i18n = require("@ori/i18n");
var _logger = require("@ori/logger");
var _lodash = _interopRequireDefault(require("lodash.debounce"));
var _react = require("react");
var _fetchPickupPoints = require("../../api/fetchPickupPoints");
var _constants = require("../../constants");
var _useInstantSearchPickupPointsQueryConfig = require("../../hooks/useInstantSearchPickupPointsQueryConfig");
var _usePickupPointsSearchConfiguration = require("../../hooks/usePickupPointsSearchConfiguration");
var _models = require("../../models");
var _utils = require("../../utils");
var _checkoutDeliveryContext = require("../checkoutDeliveryContext");
var _mapContext = require("../mapContext");
var _useGeocodingSearch = require("./useGeocodingSearch");
const logInfo = (0, _checkoutUtils.getLogger)(_constants.APP_NAME, _checkoutUtils.LogLevel.Info);
const hookName = 'usePickupPoints';
const PAGE_SIZE = 7;
const usePickupPoints = enabled => {
  const logger = (0, _logger.useLogger)('usePickupPoints');
  const [firstLoad, setFirstLoad] = (0, _react.useState)(true);
  const [loadingGeocachingResult, setLoadingGeocachingResult] = (0, _react.useState)(false);
  const [currentLatitude, setCurrentLatitude] = (0, _react.useState)('0');
  const [previousLatitude, setPreviousLatitude] = (0, _react.useState)(null);
  const [currentLongitude, setCurrentLongitude] = (0, _react.useState)('0');
  const [previousLongitude, setPreviousLongitude] = (0, _react.useState)(null);
  const [searchTerm, setSearchTerm] = (0, _react.useState)(undefined);
  const [isMapInfoOpen, setIsMapInfoOpen] = (0, _react.useState)(false);
  const [previousSearchTerm, setPreviousSearchTerm] = (0, _react.useState)(undefined);
  const {
    pickupPointsMapProps: {
      setSelectedMapPickupPoint
    },
    selectedDeliveryChannel = undefined
  } = (0, _mapContext.useMap)();
  const [selectedPickupPointId, setSelectedPickupPointId] = (0, _react.useState)(selectedDeliveryChannel?.deliveryChannelId ?? null);
  const [pickupPointsIds, setPickupPointsIds] = (0, _react.useState)([]);
  const {
    viewSettings: {
      location: {
        latitude,
        longitude
      }
    }
  } = (0, _usePickupPointsSearchConfiguration.usePickupPointsSearchConfiguration)();
  const {
    language
  } = (0, _i18n.useLanguage)();
  const {
    graphQlNetUrl
  } = (0, _configurationManager.useServiceUrls)(['graphQlNet']);
  const {
    customerId,
    sessionId = ''
  } = (0, _checkoutDeliveryContext.useCheckoutDeliveryContext)();
  const {
    token
  } = (0, _auth.useToken)();
  const {
    mapProvider,
    tenant
  } = (0, _useInstantSearchPickupPointsQueryConfig.useInstantSearchPickupPointsQueryConfig)();
  const headers = (0, _react.useMemo)(() => (0, _fetching.getHeaders)({
    language,
    customerId,
    sourceAppName: _constants.APP_NAME,
    tenant,
    token
  }), [language, customerId, tenant, token]);
  const {
    searchByGeocoding
  } = (0, _useGeocodingSearch.useGeocodingSearch)();
  const {
    isValidating: loadingPickupPoints,
    data = [],
    setSize
  } = (0, _infinite.useSWRInfinite)((pageIndex, previousPageData) => {
    const newTotalRecords = previousPageData?.totalRecords ?? 0;
    const newLoadedRecords = pageIndex * PAGE_SIZE;

    // Do not fetch if this is the first load or if the hook is disabled
    if (!enabled || firstLoad) {
      return null;
    }

    // Do not fetch if location is not set
    if (currentLatitude === '0' || currentLongitude === '0') {
      return null;
    }

    // Do not fetch if we already have fetched all pages
    if (newLoadedRecords >= newTotalRecords && pageIndex > 0) {
      return null;
    }
    return {
      headers,
      graphQlNetUrl,
      params: {
        latitude: Number.parseFloat(currentLatitude),
        longitude: Number.parseFloat(currentLongitude),
        sessionId,
        top: PAGE_SIZE,
        skip: pageIndex * PAGE_SIZE,
        radius: _constants.SEARCH_RADIUS,
        searchTerm
      }
    };
  }, _fetchPickupPoints.fetchPickupPoints, {
    // fix type in utils
    ...(0, _checkoutUtils.getRetryErrorSwrOptions)(),
    revalidateFirstPage: false,
    onSuccess: newData => {
      setLoadingGeocachingResult(true);
      logInfo(`${hookName} hook - loaded data:`, newData);
      const newPickupPointsIds = newData.flatMap(page => page.pickupPoints.map(({
        deliveryChannelId
      }) => deliveryChannelId));
      setPickupPointsIds(newPickupPointsIds);
      if (searchTerm != null && searchTerm.length > 0 && !firstLoad && newPickupPointsIds.length === 0) {
        const currentSearchTerm = mapProvider === _models.MapProviderType.Bing ? `${searchTerm},${(0, _utils.getCountryNameByTenant)(tenant)}` : searchTerm;
        searchByGeocoding({
          searchTerm: currentSearchTerm
        }).catch(error => {
          logger.error(new Error('Error while setting view settings based on geocaching', {
            cause: error
          }));
        }).finally(() => {
          setLoadingGeocachingResult(false);
        });
      } else {
        setLoadingGeocachingResult(false);
      }
    }
  });
  const pickupPoints = (0, _react.useMemo)(() => data.flatMap(page => page.pickupPoints), [data]);
  const totalRecords = data[0]?.totalRecords ?? 0;

  // request another page of data
  const loadMore = (0, _react.useCallback)(() => {
    void setSize(size => size + 1);
  }, [setSize]);
  const reset = (0, _react.useCallback)(() => {
    setFirstLoad(true);
    setSearchTerm(undefined);
  }, []);
  const search = (0, _react.useCallback)(newSearchTerm => {
    if (newSearchTerm !== previousSearchTerm) {
      reset();
      setSelectedPickupPointId(null);
      setSelectedMapPickupPoint(undefined);
      setSearchTerm(newSearchTerm);
      setPreviousSearchTerm(newSearchTerm);
    }
  }, [previousSearchTerm, reset, setSelectedMapPickupPoint]);
  const searchDebounced = (0, _lodash.default)(search, 1000);
  const clearSearch = (0, _react.useCallback)(() => {
    setSearchTerm(undefined);
    setPreviousSearchTerm(undefined);
  }, []);
  const selectPickupPoint = (0, _react.useCallback)(pickupPointId => {
    setSelectedPickupPointId(pickupPointId);
    if (searchTerm !== undefined && pickupPoints.length === 0) {
      clearSearch();
    }
  }, [clearSearch, pickupPoints.length, searchTerm]);

  // Load initial data when latitude and longitude are available
  (0, _react.useEffect)(() => {
    if (enabled && firstLoad && latitude !== 0 && longitude !== 0) {
      setFirstLoad(false);
      setCurrentLatitude(latitude.toFixed(_constants.PICKUP_POINT_COORDINATES_DECIMALS));
      setCurrentLongitude(longitude.toFixed(_constants.PICKUP_POINT_COORDINATES_DECIMALS));
      setPreviousLatitude(latitude.toFixed(_constants.PICKUP_POINT_COORDINATES_DECIMALS));
      setPreviousLongitude(longitude.toFixed(_constants.PICKUP_POINT_COORDINATES_DECIMALS));
      loadMore();
    }
  }, [enabled, firstLoad, latitude, loadMore, longitude]);
  // Reset data when latitude or longitude changes and selected pickup point is not in the list
  (0, _react.useEffect)(() => {
    if (!firstLoad && previousLatitude != null && previousLongitude != null && (latitude.toFixed(_constants.PICKUP_POINT_COORDINATES_DECIMALS) !== previousLatitude || longitude.toFixed(_constants.PICKUP_POINT_COORDINATES_DECIMALS) !== previousLongitude) && !pickupPointsIds.includes(selectedPickupPointId ?? Number.POSITIVE_INFINITY)) {
      reset();
      setCurrentLatitude(latitude.toFixed(_constants.PICKUP_POINT_COORDINATES_DECIMALS));
      setCurrentLongitude(longitude.toFixed(_constants.PICKUP_POINT_COORDINATES_DECIMALS));
    }
  }, [firstLoad, latitude, longitude, pickupPointsIds, previousLatitude, previousLongitude, reset, selectedPickupPointId]);
  return (0, _react.useMemo)(() => ({
    loadedPickupPoints: pickupPoints.length,
    loading: loadingPickupPoints || firstLoad || loadingGeocachingResult,
    pickupPoints,
    selectedPickupPointId,
    totalRecords,
    clearSearch,
    loadMore,
    isMapInfoOpen,
    setIsMapInfoOpen,
    search: searchDebounced,
    setSelectedPickupPointId: selectPickupPoint
  }), [clearSearch, firstLoad, isMapInfoOpen, loadMore, loadingGeocachingResult, loadingPickupPoints, pickupPoints, searchDebounced, selectPickupPoint, selectedPickupPointId, totalRecords]);
};
exports.usePickupPoints = usePickupPoints;